import React, { useRef, useState, useEffect } from 'react';
import Table from 'react-bootstrap/Table';
import Spinner from 'react-bootstrap/Spinner';
import 'bootstrap/dist/css/bootstrap.min.css';
import styles from './CheckableDataTable.module.scss';
import { INPUT_KEYS } from '../../constants/inputKeys';
import { formatDateTime } from '../../utils/dataFormatter';
import { formatAmount } from '../../utils/amountFormatter';
import { DATE_TIME_TYPES } from '../../constants/dataTimeTypes';
import moment from 'moment';

const CheckableDataTable = ({
  tableData,
  tableheading,
  tag,
  fetchError,
  search_filter_loading,
  notFoundMessage,
  handleOnClick,
  renderTerminalsDropdown,
  renderDeeplinkColumn,
  handleCheck,
  handleSelectAll,
  handleMultipleCheck,
  checkedRows,
  handleLocationUpdate,
  handleEmailUpdate,
  handleMobileUpdate,
  isDemoUser,
}) => {

  // editable contact fields to avoid duplicate literals
  const INIT_EDITING_CONTACTS = { id: null, index: null, value: '' };

  // State for drag-to-select behavior
  const [isDragging, setIsDragging] = useState(false);
  const [startPoint, setStartPoint] = useState({ x: 0, y: 0 });
  const [selectionBox, setSelectionBox] = useState(null);
  const containerRef = useRef(null);
  const selectAllCheckboxRef = useRef(null);

  // Inline edit states for sub-merchant fields
  const [editingLocationId, setEditingLocationId] = useState(null);
  const [locationInputValue, setLocationInputValue] = useState('');
  const [editingEmailData, setEditingEmailData] = useState(() => ({ ...INIT_EDITING_CONTACTS }));
  const [editingMobileData, setEditingMobileData] = useState(() => ({ ...INIT_EDITING_CONTACTS }));
  const inputRef = useRef(null);

  // Handle indeterminate checkbox state via ref
  useEffect(() => {
    if (selectAllCheckboxRef.current) {
      const idField = tag === 'greetly-vouchers' ? 'greetlyId' : 'id';
      selectAllCheckboxRef.current.indeterminate =
        checkedRows.length > 0 && !tableData.every((row) =>
          checkedRows.includes(row[idField])
        );
    }
  }, [checkedRows, tableData, tag]);

  // Handle clicks outside the input field
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (inputRef.current && !inputRef.current.contains(event.target)) {
        setEditingLocationId(null);
        setEditingEmailData({ ...INIT_EDITING_CONTACTS });
        setEditingMobileData({ ...INIT_EDITING_CONTACTS });
      }
    };

    const isEditing = editingLocationId !== null ||
      editingEmailData.id !== null ||
      editingMobileData.id !== null;

    if (isEditing) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [editingLocationId, editingEmailData.id, editingMobileData.id]);

  // Handlers for location editing
  const handleLocationCellClick = (id, currentValue) => {
    setEditingLocationId(id);
    setLocationInputValue(currentValue || '');
  };

  const handleLocationInputChange = (e) => {
    setLocationInputValue(e.target.value);
  };

  const handleLocationInputBlur = (id, prevValue) => {
    if (locationInputValue !== prevValue && locationInputValue.trim() !== '') {
      handleLocationUpdate(id, locationInputValue.trim());
    }
    setEditingLocationId(null);
  };

  const handleLocationInputKeyDown = (e, id, prevValue) => {
    if (e.key === INPUT_KEYS.ENTER_KEY) {
      e.target.blur();
    } else if (e.key === INPUT_KEYS.ESCAPE_KEY) {
      setEditingLocationId(null);
    }
  };

  // Handlers for email editing
  const handleEmailCellClick = (id, contactDetails, index) => {
    if (!contactDetails) return;
    setEditingEmailData({
      id,
      index,
      value: (contactDetails[index] && contactDetails[index].email) || ''
    });
  };

  const handleEmailInputChange = (e) => {
    const value = e.target.value;
    setEditingEmailData(prev => ({ ...prev, value }));
  };

  const handleEmailInputBlur = (id, prevContactDetails, index) => {
    if (!prevContactDetails) return;
    const prevValue = prevContactDetails[index] && prevContactDetails[index].email;
    if (editingEmailData.value && editingEmailData.value !== prevValue && editingEmailData.value.trim() !== '') {
      const newContacts = [...(prevContactDetails || [])];
      const existing = newContacts[index] || {};
      newContacts[index] = { ...existing, email: editingEmailData.value.trim() };
      handleEmailUpdate(id, newContacts);
    }
    setEditingEmailData({ ...INIT_EDITING_CONTACTS });
  };

  const handleEmailInputKeyDown = (e, id, prevContactDetails, index) => {
    if (e.key === INPUT_KEYS.ENTER_KEY) {
      e.preventDefault();
      e.target.blur();
    } else if (e.key === INPUT_KEYS.ESCAPE_KEY) {
      setEditingEmailData({ ...INIT_EDITING_CONTACTS });
    }
  };

  // Handlers for mobile editing
  const handleMobileCellClick = (id, contactDetails, index) => {
    if (!contactDetails) return;
    setEditingMobileData({
      id,
      index,
      value: (contactDetails[index] && contactDetails[index].mobileNo) || ''
    });
  };

  const handleMobileInputChange = (e) => {
    const value = e.target.value;
    setEditingMobileData(prev => ({ ...prev, value }));
  };

  const handleMobileInputBlur = (id, prevContactDetails, index) => {
    if (!prevContactDetails) return;
    const prevValue = prevContactDetails[index] && prevContactDetails[index].mobileNo;
    if (editingMobileData.value && editingMobileData.value !== prevValue && editingMobileData.value.trim() !== '') {
      const newContacts = [...(prevContactDetails || [])];
      const existing = newContacts[index] || {};
      newContacts[index] = { ...existing, mobileNo: editingMobileData.value.trim() };
      handleMobileUpdate(id, newContacts);
    }
    setEditingMobileData({ ...INIT_EDITING_CONTACTS });
  };

  const handleMobileInputKeyDown = (e, id, prevContactDetails, index) => {
    if (e.key === INPUT_KEYS.ENTER_KEY) {
      e.preventDefault();
      e.target.blur();
    } else if (e.key === INPUT_KEYS.ESCAPE_KEY) {
      setEditingMobileData({ ...INIT_EDITING_CONTACTS });
    }
  };

  // Start drawing the selection box
  const onMouseDown = (e) => {
    if (e.button !== 0) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    setStartPoint({ x, y });
    setSelectionBox({ x, y, width: 0, height: 0 });
    setIsDragging(true);
    e.preventDefault();
  };

  // Update selection box dimensions while dragging
  const onMouseMove = (e) => {
    if (!isDragging) return;
    const rect = containerRef.current.getBoundingClientRect();
    const currX = e.clientX - rect.left;
    const currY = e.clientY - rect.top;
    setSelectionBox({
      x: Math.min(currX, startPoint.x),
      y: Math.min(currY, startPoint.y),
      width: Math.abs(currX - startPoint.x),
      height: Math.abs(currY - startPoint.y),
    });
  };

  // Finalize selection box and toggle intersecting rows (mirrors admin implementation)
  const onMouseUp = () => {
    if (!isDragging || !selectionBox) return;
    setIsDragging(false);
    const { x, y, width, height } = selectionBox;

    // Ignore accidental micro-drags (same guard as admin version)
    const MIN_DRAG_SIZE = 10;
    if (width < MIN_DRAG_SIZE && height < MIN_DRAG_SIZE) {
      setSelectionBox(null);
      return;
    }

    const parentRect = containerRef.current.getBoundingClientRect();

    const rows = Array.from(
      containerRef.current.querySelectorAll(`.${styles.table_row}[data-id]`)
    );

    const intersectingRowIds = rows
      .filter((row) => {
        const r = row.getBoundingClientRect();
        const rowX = r.left - parentRect.left;
        const rowY = r.top - parentRect.top;
        return (
          rowX < x + width &&
          rowX + r.width > x &&
          rowY < y + height &&
          rowY + r.height > y
        );
      })
      .map((row) => row.getAttribute('data-id'));

    if (intersectingRowIds.length) {
      handleMultipleCheck(intersectingRowIds);
    }

    setSelectionBox(null);
  };

  // Render column headers with optional "Select All" checkbox
  const renderTableHeadings = (idField) =>
    tableheading.column.map((col, idx) => (
      <th key={idx} className={styles.table__heading} align='center'>
        {idx === 0 && tableData?.length > 0 && (
          <input
            ref={selectAllCheckboxRef}
            type='checkbox'
            className={styles.checkbox}
            checked={tableData?.length > 0 && tableData.every((row) =>
              checkedRows.includes(row[idField])
            )}
            onChange={handleSelectAll}
          />
        )}
        {col}
      </th>
    ));

  // Render checkbox column for row selection
  const renderCheckboxColumn = (id, onClickEvent) => {
    return (
      <td>
        <div className={styles.checkbox__column}>
          <input
            className={styles.checkbox}
            type='checkbox'
            checked={checkedRows.includes(id)}
            onClick={
              onClickEvent ? (event) => handleCheck(id, event) : undefined
            }
            onChange={(event) => handleCheck(id, event)}
          />
          <span className={styles.data}>{id}</span>
        </div>
      </td>
    );
  };

  const renderVoucherDetails = (data) => {
    return (
      <tr
        className={`${styles.table_row} ${checkedRows.includes(data.id) ? 'table-primary' : ''}`}
        data-id={data.id}
        onClick={() => handleOnClick(data.id)}
        key={data.id}
      >
        {renderCheckboxColumn(data.id, true)}
        {!isDemoUser && <td>{data.sender}</td>}
        <td>
          {moment(data.createdDate)
            .add(5, 'hours')
            .add(30, 'minutes')
            .format('DD/MM/YYYY hh:mm A')}
        </td>
        {!isDemoUser && <td>{data.ownerName}</td>}
        <td>
          {data.amount
            .toFixed(2)
            .toString()
            .replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
        </td>
        <td>{data.ownerMobile}</td>
        {isDemoUser && <td>{data.loyaltyTier?.name || 'N/A'}</td>}
        {isDemoUser && <td>{data.redeemablePoints || 'N/A'}</td>}
      </tr>
    );
  };

  const renderPendingPurchases = (data) => (
    <tr
      className={`${styles.table_row} ${checkedRows.includes(data.id) ? 'table-primary' : ''}`}
      key={data.id}
      data-id={data.id}
      onClick={() => handleOnClick(data.id)}
    >
      {renderCheckboxColumn(data.id, true)}
      <td>{data.purchaseId}</td>
      <td>{formatDateTime(data.createdDate, DATE_TIME_TYPES.ISO_DATE_TIME)}</td>
      <td>{data.createdBy || data.senderName}</td>
      <td>{formatAmount(data.amount)}</td>
    </tr>
  );

  const renderIPDetails = (data) => (
    <tr
      className={styles.table_row}
      key={data.ipAddress}
      data-id={data.ipAddress}
      onClick={() => handleCheck(data.ipAddress)}
    >
      {renderCheckboxColumn(data.ipAddress)}
      <td>{data.count}</td>
      <td>{formatDateTime(data, DATE_TIME_TYPES.ISO_DATE_TIME)}</td>
      <td>{formatDateTime(data.createdAt, DATE_TIME_TYPES.ISO_DATE_TIME)}</td>
    </tr>
  );

  const renderOTPDetails = (data) => (
    <tr
      className={styles.table_row}
      key={data.recipient}
      data-id={data.recipient}
      onClick={() => handleCheck(data.recipient)}
    >
      {renderCheckboxColumn(data.recipient)}
      <td>{data.time}</td>
      <td>{data.invalid}</td>
      <td>{data.sent}</td>
      <td>{formatDateTime(data.createdAt, DATE_TIME_TYPES.ISO_DATE_TIME)}</td>
    </tr>
  );

  const renderSubMerchantData = (data) => (
    <tr className={styles.table_row} key={data.id} data-id={data.id}>
      {renderCheckboxColumn(data.id)}
      <td>
        {data.contactDetails && data.contactDetails.length > 0 ? (
          data.contactDetails.map((contact, index) => (
            <div key={contact.id || index} className={styles.editableItem}>
              {editingEmailData.id === data.id && editingEmailData.index === index ? (
                <input
                  ref={inputRef}
                  type='email'
                  value={editingEmailData.value}
                  autoFocus
                  onChange={handleEmailInputChange}
                  onBlur={() => handleEmailInputBlur(data.id, data.contactDetails, index)}
                  onKeyDown={(e) => handleEmailInputKeyDown(e, data.id, data.contactDetails, index)}
                  style={{ width: '100%' }}
                  onClick={(e) => e.stopPropagation()}
                />
              ) : (
                <span
                  className={styles.subMerchantData}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEmailCellClick(data.id, data.contactDetails, index);
                  }}
                  title='Click to edit email'
                >
                  {contact.email || 'N/A'}
                </span>
              )}
            </div>
          ))
        ) : (
          <span>N/A</span>
        )}
      </td>
      <td>
        {data.contactDetails && data.contactDetails.length > 0 ? (
          data.contactDetails.map((contact, index) => (
            <div key={contact.id || index} className={styles.editableItem}>
              {editingMobileData.id === data.id && editingMobileData.index === index ? (
                <input
                  ref={inputRef}
                  type='tel'
                  value={editingMobileData.value}
                  autoFocus
                  onChange={handleMobileInputChange}
                  onBlur={() => handleMobileInputBlur(data.id, data.contactDetails, index)}
                  onKeyDown={(e) => handleMobileInputKeyDown(e, data.id, data.contactDetails, index)}
                  style={{ width: '100%' }}
                  onClick={(e) => e.stopPropagation()}
                />
              ) : (
                <span
                  className={styles.subMerchantData}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleMobileCellClick(data.id, data.contactDetails, index);
                  }}
                  title='Click to edit mobile number'
                >
                  {contact.mobileNo || 'N/A'}
                </span>
              )}
            </div>
          ))
        ) : (
          <span>N/A</span>
        )}
      </td>
      <td>
        {editingLocationId === data.id ? (
          <input
            ref={inputRef}
            type='text'
            value={locationInputValue}
            autoFocus
            onChange={handleLocationInputChange}
            onBlur={() => handleLocationInputBlur(data.id, data.location)}
            onKeyDown={(e) => handleLocationInputKeyDown(e, data.id, data.location)}
            style={{ width: '100%' }}
          />
        ) : (
          <span
            className={styles.subMerchantData}
            onClick={() => handleLocationCellClick(data.id, data.location)}
            title='Click to edit location'
          >
            {data.location || 'N/A'}
          </span>
        )}
      </td>
      <td data-label='Terminals' style={{ width: '10px' }}>
        {renderTerminalsDropdown(data.terminals) || 'N/A'}
      </td>
    </tr>
  );

  const renderOrders = (data) => (
    <tr
      className={`${styles.table_row} ${checkedRows.includes(data.purchaseId) ? 'table-primary' : ''}`}
      key={data.id}
      data-id={data.purchaseId}
      onClick={() => handleOnClick(data.purchaseId)}
    >
      {renderCheckboxColumn(data.purchaseId, true)}
      <td>{formatDateTime(data.createdDate, DATE_TIME_TYPES.ISO_DATE_TIME)}</td>
      <td>
        {data.deliveryDate
          ? formatDateTime(data.deliveryDate, DATE_TIME_TYPES.ISO_DATE_TIME)
          : 'Contact customer when ready'}
      </td>
      <td>{data.voucherId}</td>
      <td>{data.receiverName}</td>
      <td>
        {data.physicalOption === 'STORE_PICKUP'
          ? 'Pickup'
          : data.physicalOption === 'COURIER_DELIVERY'
            ? 'Courier'
            : 'N/A'}
      </td>
      <td>{data.isSameDayDelivery ? 'Yes' : 'No'}</td>
      <td>{data.status}</td>
      <td onClick={(e) => e.stopPropagation()}>
        {renderDeeplinkColumn(data.voucherId)}
      </td>
    </tr>
  );

  const renderTopups = (data) => {
    return (
      <tr
        className={`${styles.table_row} ${checkedRows.includes(data.id) ? 'table-primary' : ''}`}
        key={data.id}
        data-id={data.id}
        onClick={() => handleOnClick(data.id)}
      >
        {renderCheckboxColumn(data.id, true)}
        <td>{data?.client.clientName}</td>
        <td>{data?.client.id}</td>
        <td>{formatDateTime(data?.time, DATE_TIME_TYPES.ISO_DATE_TIME)}</td>
        <td>{data?.topUpValue}</td>
      </tr>
    );
  };

  const renderTableData = () => {
    switch (tag) {
      case 'ip-logs':
        return tableData.map(renderIPDetails);
      case 'otp-logs':
        return tableData.map(renderOTPDetails);
      case 'voucher-details':
        return tableData.map(renderVoucherDetails);
      case 'pending-purchases':
        return tableData.map(renderPendingPurchases);
      case 'subMerchants':
        return tableData.map(renderSubMerchantData);
      case 'orders':
        return tableData.map(renderOrders);
      case 'topups':
        return tableData.map(renderTopups);
      default:
        return null;
    }
  };

  const renderTableHeader = () => {
    switch (tag) {
      case 'ip-logs':
        return renderTableHeadings('ipAddress');
      case 'otp-logs':
        return renderTableHeadings('recipient');
      case 'voucher-details':
      case 'pending-purchases':
      case 'subMerchants':
      case 'topups':
        return renderTableHeadings('id');
      case 'orders':
        return renderTableHeadings('purchaseId');
      default:
        return null;
    }
  };

  const renderNotFound = (message) => (
    <tr>
      <td
        colSpan={tableheading.column.length}
        className='text-center text-secondary'
      >
        {message}
      </td>
    </tr>
  );

  const renderTable = () => (
    <div
      ref={containerRef}
      className='w-100'
      style={{ position: 'relative' }}
      onMouseDown={
        ['voucher-details', 'pending-purchases', 'topups', 'orders'].includes(tag)
          ? onMouseDown
          : undefined
      }
      onMouseMove={
        ['voucher-details', 'pending-purchases', 'topups', 'orders'].includes(tag)
          ? onMouseMove
          : undefined
      }
      onMouseUp={
        ['voucher-details', 'pending-purchases', 'topups', 'orders'].includes(tag)
          ? onMouseUp
          : undefined
      }
    >
      {selectionBox && (
        <div
          style={{
            position: 'absolute',
            left: selectionBox.x,
            top: selectionBox.y,
            width: selectionBox.width,
            height: selectionBox.height,
            backgroundColor: 'rgba(0,123,255,0.2)',
            border: '1px solid #007bff',
            pointerEvents: 'none',
            zIndex: 10,
          }}
        />
      )}
      <Table striped bordered hover>
        <thead>
          <tr>{renderTableHeader()}</tr>
        </thead>
        <tbody>
          {search_filter_loading ? (
            <tr>
              <td colSpan={tableheading.column.length} className='text-center'>
                <Spinner animation='border' />
              </td>
            </tr>
          ) : fetchError === '' && tableData?.length > 0 ? (
            renderTableData()
          ) : (
            renderNotFound(notFoundMessage || `NO ${tag.toUpperCase()} FOUND`)
          )}
        </tbody>
      </Table>
    </div>
  );

  return <div className={styles.root__div}>{renderTable()}</div>;
};

export default CheckableDataTable;
